import { addMonths, isWithinInterval } from 'date-fns'
import React, { createContext, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { getDisplayedDateRangeByDate } from 'src/utils/getDisplayedDateRangeByDate/getDisplayedDateRangeByDate'
import { resetSelectedBookable, setPreferredPractitioners, useChoiceDispatch, useChoiceState } from '../ChoiceContext'
import useAvailabilities from '@hooks/useAvailabilities/useAvailabilities'
import { getPeriodOffsetByPeriod } from './helpers/getPeriodOffsetByPeriod/getPeriodOffsetByPeriod'
import { getCurrentPeriod } from './helpers/getCurrentPeriod/getCurrentPeriod'
import { getShouldFetchMore } from './helpers/getShouldFetchMore/getShouldFetchMore'
import { getShouldResetSelectedBookable } from './helpers/getShouldResetSelectedBookable/getShouldResetSelectedBookable'
import { getPeriodWithNextAvailableSlot } from './helpers/getPeriodWithNextAvailableSlot'
import { setAvailabilitiesData, usePractitionersDispatch } from '../PractitionersContext'
import { useConfig } from '../ConfigurationContext'
import { AppMode } from '@customTypes/Generic'
import {
  PeriodSize,
  getAvailabilitiesGroupedBySize,
} from './helpers/getAvailabilitiesGroupedBySize/getAvailabilitiesGroupedBySize'
import { CityClinic } from '@customTypes/Clinic'
import { AvailabilitiesPeriod } from './types'
import { first, last } from 'lodash'
import { calculateDayWithNextAvailableSlot } from './helpers/calculateDayWithNextAvailableSlot/calculateDayWithNextAvailableSlot'
import { useCityClinicsForSelectedTreatment } from '@hooks/useCityClinicsForSelectedTreatment'
import { hasAvailabilitiesBefore } from './helpers/hasAvailabilitiesBefore/hasAvailabilitiesBefore'
import { fromZonedTime } from 'date-fns-tz'
import { PatientTypeOptions } from 'src/config/patientTypeOptions'
import { CALENDAR_DIRECTIONS } from '@customTypes/AppointmentCalendarDirections'

export const TRANSITION_TIME_IN_SECONDS = 0.25

type ClinicExpandedState = Record<string, boolean>

type AppointmentCalendarContextProps = {
  goToNextPeriod?: () => void
  goToPrevPeriod?: () => void
  goToPeriodWithNextAvailableSlot: (clinicId: string) => void
  getDayWithNextAvailableSlot: (clinicId: string) => Date | undefined
  currentPeriod: AvailabilitiesPeriod
  isLoading?: boolean
  isFetching?: boolean
  refresh: () => void
  hasMoreClinicsInCity: boolean
  isMoreClinicsOpen: boolean
  toggleMoreClinicsOpen: () => void
  clinicsInCity: CityClinic[]
  clinicExpandedState: ClinicExpandedState
  toggleClinicExpanded: (clinicId: string) => void
  shouldScrollToNearbyClinics: boolean
  resetShouldScrollToNearbyClinics: () => void
  cityClinicsLoading: boolean
  swipeToPeriod: (direction: string) => void
  isTransitioning: boolean
  transition: CALENDAR_DIRECTIONS | undefined
}

export const defaultCalendarContext: AppointmentCalendarContextProps = {
  currentPeriod: {},
  refresh: () => {},
  hasMoreClinicsInCity: false,
  isMoreClinicsOpen: false,
  toggleMoreClinicsOpen: () => {},
  goToPeriodWithNextAvailableSlot: (clinicId: string) => {},
  getDayWithNextAvailableSlot: (clinicId: string) => new Date(),
  clinicsInCity: [],
  clinicExpandedState: {},
  toggleClinicExpanded: (clinicId: string) => {},
  shouldScrollToNearbyClinics: false,
  resetShouldScrollToNearbyClinics: () => {},
  cityClinicsLoading: false,
  swipeToPeriod: (direction: string) => {},
  isTransitioning: false,
  transition: undefined,
}

export const AppointmentCalendarContext = createContext<AppointmentCalendarContextProps>(defaultCalendarContext)

type AppointmentCalendarProviderProps = { children?: ReactElement }

export const AppointmentCalendarProvider = ({ children }: AppointmentCalendarProviderProps): ReactElement => {
  const { selectedBookableDateAndTime, patientType } = useChoiceState()
  const { mode, clinicId } = useConfig()
  const practitionersDispatch = usePractitionersDispatch()
  const choiceDispatch = useChoiceDispatch()
  const { data: clinicsInCity, isLoading: cityClinicsLoading } = useCityClinicsForSelectedTreatment()
  const [isMoreClinicsOpen, setIsMoreClinicsOpen] = useState(false)
  const [clinicExpandedState, setClinicExpandedState] = useState<ClinicExpandedState>({})
  const [shouldScrollToNearbyClinics, setShouldScrollToNearbyClinics] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [transition, setTransition] = useState<CALENDAR_DIRECTIONS | undefined>(undefined)
  const { clinicId: clinicIdOfSelectedSlot } = selectedBookableDateAndTime
  const resetCurrentPeriod = useCallback(() => {
    setCurrentPeriod({})
  }, [])

  const {
    data: infiniteData,
    fetchNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    refetch,
  } = useAvailabilities(
    (lastAvailabilityGroup) => {
      const nextMonthDate = addMonths(lastAvailabilityGroup[clinicId].toDate, 1)
      return getDisplayedDateRangeByDate(nextMonthDate)
    },
    isMoreClinicsOpen,
    clinicsInCity || [],
    resetCurrentPeriod
  )

  const data = infiniteData?.pages

  useEffect(() => {
    const mappedData = (data || []).map((el) => el[clinicId])
    practitionersDispatch(setAvailabilitiesData(mappedData))
  }, [data?.length, data, practitionersDispatch, clinicId])

  const availabilitiesGrouped = useMemo(() => getAvailabilitiesGroupedBySize(data, PeriodSize.Week), [data])

  const [consistentFetching, setConsistentFetching] = useState(isFetching)

  useEffect(() => {
    if (isFetching) {
      setConsistentFetching(true)
      return
    }
    const setFetchingTimeout = setTimeout(() => setConsistentFetching(false), 200)
    return () => {
      clearTimeout(setFetchingTimeout)
    }
  }, [isFetching])

  const selectedDate = useMemo(
    () =>
      selectedBookableDateAndTime.date && selectedBookableDateAndTime.time
        ? fromZonedTime(
            new Date(`${selectedBookableDateAndTime.date}T${selectedBookableDateAndTime.time}`),
            'Europe/Berlin'
          )
        : undefined,
    [selectedBookableDateAndTime.date, selectedBookableDateAndTime.time]
  )

  const [isMonthWithSelectionLoaded, setIsMonthWithSelectionLoaded] = useState(false)

  useEffect(() => {
    if (selectedDate && !isMonthWithSelectionLoaded && data) {
      const isLoaded = data.some((clinicsAvailabilities) =>
        Object.values(clinicsAvailabilities).some((clinicAvaillabilities) =>
          isWithinInterval(selectedDate, { start: clinicAvaillabilities.fromDate, end: clinicAvaillabilities.toDate })
        )
      )
      if (isLoaded) setIsMonthWithSelectionLoaded(true)
    }
  }, [data, selectedDate, isMonthWithSelectionLoaded])

  const [currentPeriod, setCurrentPeriod] = useState<AvailabilitiesPeriod>({})

  const firstPeriodRange = Object.values(currentPeriod)[0]
  const periodRangeStart = first(firstPeriodRange)?.date
  const periodRangeEnd = last(firstPeriodRange)?.date
  const currentPeriodClinicsLength = Object.keys(currentPeriod).length

  useEffect(() => {
    const newPeriod = getCurrentPeriod(availabilitiesGrouped, periodRangeStart, currentPeriodClinicsLength)

    if (newPeriod) {
      setCurrentPeriod(newPeriod)
    }
  }, [
    availabilitiesGrouped,
    isMonthWithSelectionLoaded,
    clinicIdOfSelectedSlot,
    periodRangeStart,
    currentPeriodClinicsLength,
  ])

  useEffect(() => {
    if (mode !== AppMode.GAP_FILLER) {
      const shouldFetchMore = getShouldFetchMore(
        currentPeriod,
        selectedDate,
        data,
        isFetchingNextPage,
        periodRangeStart
      )
      shouldFetchMore && fetchNextPage()
    }
  }, [data, currentPeriod, fetchNextPage, selectedDate, isFetchingNextPage, mode, periodRangeStart])

  const dispatch = useChoiceDispatch()

  useEffect(() => {
    const shouldResetSelectedBookable = getShouldResetSelectedBookable(
      data,
      selectedBookableDateAndTime,
      selectedDate,
      isFetchingNextPage || isFetching
    )

    shouldResetSelectedBookable && dispatch(resetSelectedBookable())
  }, [data, selectedBookableDateAndTime, selectedDate, dispatch, isFetchingNextPage, isFetching])

  const nextPeriod = useMemo(
    () => getPeriodOffsetByPeriod(currentPeriod, availabilitiesGrouped),
    [currentPeriod, availabilitiesGrouped]
  )

  const goToNextPeriod = useMemo(() => {
    return nextPeriod && Object.values(nextPeriod).some((av) => av?.length)
      ? () => setCurrentPeriod(nextPeriod)
      : undefined
  }, [nextPeriod])

  const prevPeriod = useMemo(
    () => getPeriodOffsetByPeriod(currentPeriod, availabilitiesGrouped, true),
    [currentPeriod, availabilitiesGrouped]
  )

  const goToPrevPeriod = useMemo(() => {
    return prevPeriod && hasAvailabilitiesBefore(periodRangeStart, availabilitiesGrouped)
      ? () => setCurrentPeriod(prevPeriod)
      : undefined
  }, [prevPeriod, periodRangeStart, availabilitiesGrouped])

  const goToPeriodWithNextAvailableSlot = useCallback(
    (clinicId: string) => {
      const dayWithNextAvailableSlot = calculateDayWithNextAvailableSlot(
        clinicId,
        availabilitiesGrouped,
        periodRangeEnd
      )
      const periodWithNextAvailableSlots = getPeriodWithNextAvailableSlot(
        clinicId,
        availabilitiesGrouped,
        dayWithNextAvailableSlot
      )
      if (periodWithNextAvailableSlots) {
        setCurrentPeriod(periodWithNextAvailableSlots)
      }
    },
    [availabilitiesGrouped, periodRangeEnd]
  )

  const getDayWithNextAvailableSlot = useCallback(
    (clinicId: string) => {
      const dayWithNextAvailableSlot = calculateDayWithNextAvailableSlot(
        clinicId,
        availabilitiesGrouped,
        periodRangeEnd
      )
      return dayWithNextAvailableSlot ? new Date(dayWithNextAvailableSlot) : undefined
    },
    [availabilitiesGrouped, periodRangeEnd]
  )

  const refresh = useCallback(async () => {
    await refetch()
    resetCurrentPeriod()
  }, [refetch, resetCurrentPeriod])

  const toggleMoreClinicsOpen = () => {
    if (!isMoreClinicsOpen) {
      choiceDispatch(setPreferredPractitioners([]))
      setShouldScrollToNearbyClinics(true)
    } else {
      setShouldScrollToNearbyClinics(false)
    }
    setIsMoreClinicsOpen((v) => !v)
  }

  const toggleClinicExpanded = (clinicId: string) => {
    const previousValue = !!clinicExpandedState[clinicId]
    setClinicExpandedState({ ...clinicExpandedState, [clinicId]: !previousValue })
  }

  useEffect(() => {
    if (patientType === PatientTypeOptions.OLD && isMoreClinicsOpen) setIsMoreClinicsOpen(false)
  }, [patientType, isMoreClinicsOpen])

  const swipeToPeriod = (direction: string) => {
    if (!direction) return
    if (direction === CALENDAR_DIRECTIONS.PREV && !goToPrevPeriod) return

    setIsTransitioning(true)

    setTransition(direction === CALENDAR_DIRECTIONS.NEXT ? CALENDAR_DIRECTIONS.LEFT : CALENDAR_DIRECTIONS.RIGHT)

    setTimeout(() => {
      if (direction === CALENDAR_DIRECTIONS.NEXT && goToNextPeriod) {
        goToNextPeriod()
      } else if (direction === CALENDAR_DIRECTIONS.PREV && goToPrevPeriod) {
        goToPrevPeriod()
      }
    }, TRANSITION_TIME_IN_SECONDS * 1000)
  }

  useEffect(() => {
    if (transition) {
      setIsTransitioning(false)
    }
  }, [currentPeriod, transition])

  return (
    <AppointmentCalendarContext.Provider
      value={{
        isLoading,
        isFetching: consistentFetching,
        currentPeriod,
        goToNextPeriod,
        goToPrevPeriod,
        goToPeriodWithNextAvailableSlot,
        getDayWithNextAvailableSlot,
        refresh,
        hasMoreClinicsInCity: (clinicsInCity?.length || 0) > 1,
        clinicsInCity: clinicsInCity || [],
        isMoreClinicsOpen,
        toggleMoreClinicsOpen,
        clinicExpandedState,
        toggleClinicExpanded,
        shouldScrollToNearbyClinics,
        resetShouldScrollToNearbyClinics: () => setShouldScrollToNearbyClinics(false),
        cityClinicsLoading,
        swipeToPeriod,
        isTransitioning,
        transition,
      }}
    >
      {children}
    </AppointmentCalendarContext.Provider>
  )
}

export const useAppointmentCalendar = (): AppointmentCalendarContextProps => {
  return useContext(AppointmentCalendarContext)
}
